import pluralize from 'pluralize'
import ApiClient from '@softwaredev_cegeka/api-client'
import serializeParamsWithoutBrackets from '@softwaredev_cegeka/api-client/dist/utils/axios/serializeParamsWithoutBrackets'
import objectFilterMapper from './filterMappers/ObjectFilterMapper'

export const CUSTOMER = 'customer'
export const DEVICE = 'device'
export const MAINTENANCE = 'maintenance'
export const CONFIGURATION = 'configuration'
export const TICKET = 'ticket'
export const ALL = 'all'

export const PLUGIN_TYPE = {
    ZENOSS: 'Zenoss',
    DYNATRACE: 'Dynatrace',
}

class CoreApi extends ApiClient {
    static BASE_URL = '/api/core'
    static FILTER_MAPPERS = [objectFilterMapper]
    constructor(timeout) {
        super(timeout)
        this.client.defaults.paramsSerializer = {
            serialize: serializeParamsWithoutBrackets,
        }
    }
    /**
     * @param params
     * @param cancelId
     * @returns {*}
     */
    fetchConfigurations(params = {}, cancelId = null) {
        return this.client.get('/configurations', {
            params: {
                per_page: 20,
                page: 1,
                ...params,
            },
            cancelId: cancelId,
        })
    }

    /**
     * @param configurationId
     * @param cancelId
     * @returns {*}
     */
    fetchMaintenanceContacts(configurationId, cancelId = null) {
        return this.client.get(
            `/configurations/${configurationId}/monitoring_team`,
            { cancelId: cancelId }
        )
    }

    /**
     * @param params
     * @param cancelId
     * @returns {*}
     */
    fetchCustomers(params = {}, cancelId = null) {
        return this.client.get('/customers', {
            params: {
                per_page: 20,
                page: 1,
                load_relationships: true,
                ...params,
            },
            cancelId: cancelId,
        })
    }

    /**
     * @param params
     * @param cancelId
     * @returns {*}
     */
    fetchDevices(params = {}, cancelId = null) {
        return this.client.get('/devices', {
            params: {
                per_page: 20,
                page: 1,
                load_relationships: true,
                ...params,
            },
            cancelId: cancelId,
        })
    }

    /**
     * @param {Object} params
     * @param {string} params.device_uid
     * @param {Symbol} [cancelId]
     * @returns {Promise<never>|*}
     */
    fetchDevice(params = {}, cancelId = null) {
        const { device_uid } = params
        if (!device_uid) {
            return Promise.reject('Provide a device_uid')
        }

        return this.client.get(`/devices/${device_uid}`, {
            params: {
                per_page: 20,
                page: 1,
                load_relationships: false,
                ...params,
            },
            cancelId: cancelId,
        })
    }

    /**
     * @param {Object} params
     * @param {string} params.device_uid
     * @param {Symbol} [cancelId]
     * @returns {*}
     */
    fetchDeviceTickets(params = {}, cancelId = null) {
        const { device_uid } = params
        if (!device_uid) {
            return Promise.reject('Provide a device_uid')
        }
        return this.client.get(`/devices/${device_uid}/tickets`, {
            cancelId: cancelId,
            timeout: 30000,
        })
    }

    /**
     * Fetch values for the entity sidebar filters
     * @param {*} params
     * @param {*} cancelId
     * @returns
     */
    fetchFilterValues(params = {}, cancelId = null) {
        const { entity, data, field, input, limit = 10, ...rest } = params
        // make sure the cancelId is for this particular filter
        cancelId = `${entity}-${field}-${cancelId}`

        // Order the filters alphabetically.
        const postData = { ...data, ascending: true, order_by: field }

        // We pluralize the passed entity as it the endpoint expects it to be plural
        return this.client.post(
            `/${pluralize(entity)}/options/${field}`,
            postData,
            {
                timeout: 30000,
                params: {
                    limit,
                    value_like: input,
                    ...rest,
                },
                cancelId,
            }
        )
    }

    /**
     * @param params
     * @param cancelId
     * @returns {*}
     */
    fetchSearchFilters(params, cancelId = null) {
        return this.client.get('/search_filters', {
            params,
            cancelId: cancelId,
        })
    }

    /**
     * @param filterId
     * @param cancelId
     * @returns {*}
     */
    fetchSearchFilter(filterId, cancelId = null) {
        return this.client.get(`/search_filters/${filterId}`, {
            cancelId: cancelId,
        })
    }

    /**
     * @param existingId
     * @param postData
     * @param cancelId
     * @returns {*}
     */
    saveSearchFilters(existingId, postData, cancelId = null) {
        if (existingId) {
            return this.client.put(`/search_filters/${existingId}`, postData, {
                cancelId: cancelId,
            })
        }
        return this.client.post('/search_filters', postData, {
            cancelId: cancelId,
        })
    }

    /**
     * @param filterId
     * @param cancelId
     * @returns {*}
     */
    deleteSearchFilters(filterId, cancelId = null) {
        return this.client.delete(`/search_filters/${filterId}`, {
            cancelId: cancelId,
        })
    }

    /**
     * @param data
     * @param [cancelId]
     * @returns {*}
     */
    search(data, cancelId = null) {
        const { filters, page = 1, per_page = 50, filter, ...rest } = data

        return this.client.post(
            '/search',
            {
                page: page,
                per_page: per_page,
                query: filters.query,
                term: filters.term,
                filter: this.mapFilter(filter),
                ...rest,
            },
            {
                cancelId: cancelId,
            }
        )
    }

    startExport(params, cancelId = null) {
        const { filters, page = 1, per_page = 50, entityType, ...rest } = params
        if (
            !entityType ||
            !/(configurations|customers|devices)/.test(entityType)
        ) {
            return Promise.reject('Please provide an entityType')
        }
        return this.client.post(
            `/${entityType}/export`,
            {
                page: page,
                per_page: per_page,
                query: filters.query,
                term: filters.term,
                ...rest,
            },
            {
                cancelId: cancelId,
            }
        )
    }

    /**
     * @param {Object} params
     * @param {Object} params.filter
     * @param {int} params.per_page
     * @param {int} params.page
     * @param {Symbol} [cancelId]
     * @returns {*}
     */
    fetchExportHistory(params, cancelId = null) {
        const { page = 1, per_page = 50, filter, ...rest } = params
        return this.client.get('/documents/export-history', {
            params: {
                page: page,
                per_page: per_page,
                cancelId: cancelId,
                filter: this.mapFilter(filter),
                ...rest,
            },
        })
    }

    static utils = {
        /**
         * Get a device's plugin links matching a certain type.
         * @param device
         * @param type
         * @returns {*|*[]}
         */
        getPlugins(device, type) {
            if (!type) {
                return device?.plugin_device_links ?? []
            }
            return (
                device?.plugin_device_links?.filter(
                    plugin => plugin.plugin_type === type
                ) ?? []
            )
        },
        getDynatraceEntityFromUrl(url) {
            return url.match(/.*\/entity\/([^/]*)/)?.[1] ?? url
        },
    }
}

export { CoreApi }
